import React, {createRef, useState} from 'react';
import Button from "@mui/material/Button";
import {CardContent, CardMedia, CircularProgress, Modal, Paper} from "@mui/material";
import PhotoEditor from "../../photoEditor/PhotoEditor";
import CartManager from "../../managers/cartManager/cartManager";
import cartManager, {useCartChange} from "../../managers/cartManager/cartManager";
import FunctionHandler from "../../utils/functionHandler";
import Grid from "@mui/material/Grid2";
import {Add, ClearOutlined, DeleteOutline, DrawOutlined, FileUploadOutlined} from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import Counter from "../../components/counter/Counter";
import Typography from "@mui/material/Typography";
import {Trans} from "react-i18next";
import SelectedProductDesigns from "./SelectedProductDesigns";
import Box from "@mui/material/Box";
import ImageLoader from "../../components/imageLoader/ImageLoader";
import SelectedCustomDesignsWithFiles from "./SelectedCustomDesignsWithFiles";

const fileTypes = ["JPG", "JPEG", "PNG", "SVG", "WEBP"];

function DesignsPage(props) {
    const [openedSize, setOpenedSize] = useState(false);
    const [editorOpen, setEditorOpen] = useState(false);
    const [selected, setSelection] = useState({});
    const inputRef = createRef();
    const handleClick = React.useCallback(() => inputRef.current?.click(), [inputRef]);
    const [loading, setLoading] = useState(false);

    const cart = useCartChange();

    const totalNecessaryDesigns = CartManager.getTotalNecessaryDesigns();
    const totalNecessaryDesignsPerSize = CartManager.getTotalNecessaryDesignsPerSize();
    const totalNumberOfSelectedDesign = CartManager.getTotalNumberOfSelectedDesign();
    const totalNumberOfSelectedDesignPerSize = CartManager.getTotalNumberOfSelectedDesignPerSize();

    const handleOpenModal = (size) => {
        setOpenedSize(size);
    }

    const handleCloseModal = () => {
        setOpenedSize(false);
        setEditorOpen(false);
        setSelection({});
    }

    const closeEditor = (svg) => {
        setEditorOpen(false);
        if (svg) {
            setLoading(true);
            FunctionHandler.sendData('/uploadSvg', {svg}, (data, err) => {
                if (!err) {
                    CartManager.addDesignPath(data.filePath);
                }
                setLoading(false);
            });
        }
    }

    const onFileUpload = (event) => {
        const formData = new FormData();
        Array.from(event.target.files).forEach(file => {
                formData.append('file', file);
            }
        );
        setLoading(true);

        FunctionHandler.sendFile('/upload', formData, (data) => {
            CartManager.addDesignsPath(data.uploadedFiles);
            setLoading(false);
        });

        if (inputRef.current) {
            inputRef.current.value = "";
            inputRef.current.type = "text";
            inputRef.current.type = "file";
        }
    }

    const removeDesign = (key) => {
        cartManager.removeASelectedDesign(key);
    }

    const getSelectedDesigns = () => {
        let selectedDesigns = [];
        const sizes = Object.keys(totalNecessaryDesignsPerSize).filter(size => totalNecessaryDesignsPerSize[size] > 0);

        Object.keys(cart.selectedDesigns).forEach(key => {
            if (sizes.indexOf(cart.selectedDesigns[key].size) === -1) {
                sizes.push(cart.selectedDesigns[key].size);
            }
        });

        sizes.forEach(size => {
            selectedDesigns.push(<CardContent sx={{px: 0, width: '100%'}}>
                <Typography>{size} - {getMessage(totalNecessaryDesignsPerSize[size] || 0, totalNumberOfSelectedDesignPerSize[size] || 0)}</Typography>
            </CardContent>);
            const placeHolderItemsNr = (totalNecessaryDesignsPerSize[size] || 0) - (totalNumberOfSelectedDesignPerSize[size] || 0);
            selectedDesigns = selectedDesigns.concat(Object.keys(cart.selectedDesigns)
                .filter(key => cart.selectedDesigns[key].size === size).map((key) => {
                    const src = cart.selectedDesigns[key].src;
                    return <Grid key={key} sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}>
                        <ImageLoader
                            src={FunctionHandler.getFunctionUrl(`/dep/${encodeURIComponent('thumbnail/')}${src.replace(/.svg$/, '.png')}`)}
                            fallbackSrc={FunctionHandler.getFunctionUrl(`/dep/${src}`)}
                            sx={{
                                overflow: 'hidden',
                                width: 82,
                                height: 82,
                                borderRadius: '50%',
                                cursor: 'pointer',
                                border: '2px dashed',
                                borderColor: (placeHolderItemsNr < 0 ? 'error.light' : 'primary.main'),
                            }}/>
                        <Counter pt={1} setCount={(value) => {
                            if (value === 0) {
                                removeDesign(key);
                            } else {
                                CartManager.changeSelectedDesignNr(key, value);
                            }
                        }}
                                 disablePlus={placeHolderItemsNr <= 0}
                                 count={cart.selectedDesigns[key].nr}
                                 minValue={1}
                        />
                        <CardContent className="" sx={{
                            width: 84,
                            height: 84,
                            borderRadius: '50%',
                            cursor: 'pointer',
                            backgroundColor: 'rgba(255,255,255,0.7)',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            opacity: 0,
                            "&:hover": {
                                opacity: 1,
                            }
                        }} onClick={() => removeDesign(key)}>
                            <DeleteOutline
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)'
                                }}
                                color="secondary"/>
                        </CardContent>
                    </Grid>
                }));

            for (let i = 0; i < placeHolderItemsNr; i++) {
                selectedDesigns.push(<Grid key={`placeholder-${i}-${size}`}>
                    <CardContent sx={{
                        width: 82,
                        height: 82,
                        borderRadius: '50%',
                        cursor: 'pointer',
                        border: '2px dashed',
                        borderColor: 'warning.light'
                    }} onClick={() => handleOpenModal(size)}>
                        <Add sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)'
                        }}/>
                    </CardContent>
                </Grid>);
            }
        });

        return selectedDesigns;
    }

    const selectDesign = (src) => {
        const maxSelection = (totalNecessaryDesignsPerSize[openedSize] || 0) - (totalNumberOfSelectedDesignPerSize[openedSize] || 0);
        const selectedObjLength = Object.keys(selected).length;
        let newSelection = Object.assign({}, selected);
        if (selected[src]) {
            delete newSelection[src];
        } else if (selectedObjLength < maxSelection) {
            newSelection[src] = 1;
        } else if (maxSelection === 1) {
            newSelection = {}
            newSelection[src] = 1;
        }

        setSelection(newSelection);
    }

    const getDesigns = () => {
        return cart.designs.map((src, index) => {
            return <Grid key={src}
                         sx={{
                             padding: '2px',
                             border: '2px solid',
                             borderColor: selected[src] ? 'secondary.main' : 'transparent'
                         }}>
                <Paper>
                    <ImageLoader
                        src={FunctionHandler.getFunctionUrl(`/dep/${encodeURIComponent('thumbnail/')}${src.replace(/.svg$/, '.png')}`)}
                        fallbackSrc={FunctionHandler.getFunctionUrl(`/dep/${src}`)}
                        sx={{
                            width: 50,
                            height: 50,
                            cursor: 'pointer'
                        }}
                        onClick={() => selectDesign(src)}/>
                </Paper>
            </Grid>
        });
    }

    const handleOpenEditor = () => {
        setEditorOpen(true);
    }

    const submitSelection = () => {
        CartManager.addToSelectDesigns(selected, openedSize);
        setSelection({});
        handleCloseModal();
    }

    const getMessage = (tnd, tsd) => {
        const noNeedForDesigns = tnd === 0;
        const moreDesignThenNecessary = (tnd - tsd) < 0;
        const justRight = tnd === tsd;
        const needToSelect = tnd > tsd;

        if (moreDesignThenNecessary) {
            return <Trans i18nKey={'designsPage.moreDesign'}/>;
        }

        if (noNeedForDesigns) {
            return '';
        }

        if (justRight) {
            return <Trans i18nKey={'designsPage.justRight'}/>;
        }

        if (needToSelect) {
            return <Trans i18nKey={'designsPage.addDesignes'}/>;
        }
    }

    return (cart &&
        <div className="designspage">
            {loading ? <Box sx={{
                position: 'fixed',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 10000,
                backgroundColor: 'rgba(0, 0, 0, 0.35)'
            }}><CircularProgress/></Box> : ''}
            {Object.keys(cart?.items || {}).length ? '' : <Box>
                <CardContent sx={{px: 0}}>
                    <Typography><Trans i18nKey={'designsPage.nothingSelected'}/></Typography>
                </CardContent>
            </Box>}
            <SelectedProductDesigns cart={cart}/>
            <SelectedCustomDesignsWithFiles cart={cart}/>

            {(totalNecessaryDesigns + totalNumberOfSelectedDesign > 0) &&
                <Grid container gap={1}>{getSelectedDesigns()}</Grid>}
            <Modal
                open={openedSize}
                onClose={handleCloseModal}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}
            >
                <Paper sx={{width: {xs: '100%', sm: 500}, maxHeight: '100%', overflowY: 'auto', overflowX: 'hidden'}}>
                    {editorOpen ? '' : <IconButton onClick={handleCloseModal}
                                                   sx={{position: 'absolute', top: -5, right: -5, zIndex: 100}}>
                        <ClearOutlined/>
                    </IconButton>}
                    {editorOpen ? <CardContent sx={{
                            height: {xs: '100%', sm: 'initial'},
                            p: 0,
                            overflowY: 'auto',
                            overflowX: 'hidden'
                        }}>
                            <PhotoEditor closeEditor={closeEditor} fileTypes={fileTypes} size={openedSize}/>
                        </CardContent> :
                        <CardContent sx={{height: {xs: '100%', sm: 'initial'}, pt: 3}}>
                            <Typography variant="body2" sx={{pb: 1}}>
                                <Trans i18nKey={'design.warning'}/>
                            </Typography>
                            <Grid
                                container
                                flexDirection="column"
                                justifyContent="space-between"
                                sx={{minHeight: 450, height: {xs: '100%'}}}
                            >
                                <Grid container gap={2}>
                                    <Grid sx={{
                                        padding: '2px',
                                        border: '2px solid',
                                        borderColor: 'transparent'
                                    }}>
                                        <CardContent sx={{
                                            width: 50,
                                            height: 50,
                                            cursor: 'pointer',
                                            border: '2px dashed',
                                            borderColor: 'primary.main'
                                        }} onClick={handleOpenEditor}>
                                            <DrawOutlined sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)'
                                            }} color="secondary"/>
                                        </CardContent>
                                    </Grid>
                                    <Grid sx={{
                                        padding: '2px',
                                        border: '2px solid',
                                        borderColor: 'transparent'
                                    }}>
                                        <CardContent sx={{
                                            width: 50,
                                            height: 50,
                                            cursor: 'pointer',
                                            border: '2px dashed',
                                            borderColor: 'primary.main'
                                        }} onClick={handleClick}>
                                            <FileUploadOutlined sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)'
                                            }} color="secondary"/>
                                            <input type="file" style={{display: 'none'}} ref={inputRef}
                                                   onChange={onFileUpload}
                                                   accept={fileTypes.map(ext => `image/${ext}`).join(', ')}
                                                   multiple={true}/>
                                        </CardContent>
                                    </Grid>
                                    {getDesigns()}
                                </Grid>
                                <Box sx={{justifyContent: 'flex-end', display: 'flex', pt: 3}}>
                                    <Button onClick={submitSelection} variant={"contained"} sx={{ml: 2}}>
                                        <Trans i18nKey={'buttons.submit'}/>
                                    </Button>
                                </Box>
                            </Grid>
                        </CardContent>
                    }

                </Paper>
            </Modal>
        </div>
    );
}

export default DesignsPage;
